import {Resource} from '../../api/resource';
import {BasicProperty} from '../../basic-entity-back/annotations';
import {PropertyType} from '../../basic-entity-back/property-type/property-type';
import {ReadWrite, SearchType} from '../../basic-entity-back/basic-entity-interface/mapping-external';
import {Usuario} from './usuario.model';
import {Idioma} from '../localizacion/idioma.model';
import {DireccionPersona} from './direccion-persona.model';
import {TelefonoPersona} from './telefono-persona.model';
import {NestedBehavior} from '../../basic-entity-back/property-type/nested-model-type';
import {Uri} from '../../api/uri';
import {ExactSearchFilter} from '../../basic-entity-back/filters/search-filter';

export abstract class Persona extends Resource {
    @BasicProperty({
        name: 'ID',
        isId: true,
        type: PropertyType.Integer(),
        filters: [ExactSearchFilter]
    })
    private _id;

    @BasicProperty({
        name: 'Nombre / Razón Social',
        type: PropertyType.String(),
        sortable: true
    })
    private _nombre;

    @BasicProperty({
        name: 'Primer Apellido',
        type: PropertyType.String(),
        nullable: true
    })
    private _apellido1;

    @BasicProperty({
        name: 'Segundo Apellido',
        type: PropertyType.String(),
        nullable: true
    })
    private _apellido2;

    // `protected`, no `private`: `ClienteAsociado` la redeclara sin 'searchable'/'sortable' -la API
    // no lleva SearchFilter ni OrderFilter sobre 'cifNif' para ese recurso, sólo para Cliente y el
    // resto de personas- (ver su propio comentario). Con `private` en Persona, TypeScript no deja
    // redeclararla en la subclase.
    @BasicProperty({
        name: 'CIF/NIF',
        type: PropertyType.String(),
        searchable: SearchType.Exact,
        sortable: true,
        nullable: true
    })
    protected _cifNif;

    @BasicProperty({
        name: 'Fecha de creación',
        type: PropertyType.Date(),
        readWrite: ReadWrite.ReadOnly,
        nullable: true,
        sortable: true
    })
    private _fechaCreacion;

    @BasicProperty({
        name: 'Fecha de modificación',
        type: PropertyType.Date(),
        readWrite: ReadWrite.ReadOnly,
        nullable: true,
        sortable: true
    })
    private _fechaModificacion;

    // `protected`, no `private`: `ClienteAsociado` la redeclara sin 'searchable'/'sortable', mismo
    // motivo que 'cifNif' (ver su comentario, justo arriba).
    @BasicProperty({
        name: 'Email',
        type: PropertyType.Email(),
        nullable: false,
        searchable: SearchType.Start,
        sortable: true
    })
    protected _email;

    // `protected`, no `private`: `ClienteAsociado` necesita redeclararla con exclusiones distintas
    // ([IRI_PROPERTY, TYPE_PROPERTY], ver su propio comentario) al anidarla. TypeScript no deja
    // redeclarar en una subclase un campo que el padre marcó `private` ("Types have separate
    // declarations of a private property"); con `protected` sí, siempre que la subclase mantenga
    // la misma visibilidad o una más permisiva.
    @BasicProperty({
        name: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly),
        array: true
    })
    protected _direcciones;

    // `protected`, no `private`: `ClienteAsociado` la redeclara para añadir `tag: 'Teléfonos'`
    // (aquí no lo lleva porque `Persona.telefonos` no se edita directamente en ningún formulario).
    @BasicProperty({
        name: 'Teléfonos',
        type: PropertyType.NestedModel(TelefonoPersona, 'persona', NestedBehavior.CreateOnly),
        array: true
    })
    protected _telefonos;

    @BasicProperty({
        name: 'Usuario',
        nullable: true,
        type: PropertyType.NestedModel(Usuario, null, NestedBehavior.CreateOnly)
    })
    private _usuario;

    @BasicProperty({
        name: 'Idioma',
        type: PropertyType.Uri(Idioma),
        searchable: SearchType.Exact
    })
    private _idioma: Uri;

    @BasicProperty({
        name: 'Empresa',
        type: PropertyType.Boolean(),
        nullable: false
    })
    private _isEmpresa: boolean;

    @BasicProperty({
        name: 'Razón Social / Nombre',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly
    })
    private _nombreCompleto;

    @BasicProperty({
        name: 'Teléfonos',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get todosLosTelefonos(): string {
        return this.telefonos.map(tel => "+(" + tel.codigoArea + ") " + tel.telefono).join(', ')
    }

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get nombre() {
        return this._nombre;
    }

    set nombre(value) {
        this._nombre = value;
    }

    get apellido1() {
        return this._apellido1;
    }

    set apellido1(value) {
        this._apellido1 = value;
    }

    get apellido2() {
        return this._apellido2;
    }

    set apellido2(value) {
        this._apellido2 = value;
    }

    get cifNif() {
        return this._cifNif;
    }

    set cifNif(value) {
        this._cifNif = value;
    }

    get fechaCreacion() {
        return this._fechaCreacion;
    }

    set fechaCreacion(value) {
        this._fechaCreacion = value;
    }

    get fechaModificacion() {
        return this._fechaModificacion;
    }

    set fechaModificacion(value) {
        this._fechaModificacion = value;
    }

    get email() {
        return this._email;
    }

    set email(value) {
        this._email = value;
    }

    get direcciones() {
        return this._direcciones;
    }

    set direcciones(value) {
        this._direcciones = value;
    }

    get telefonos() {
        return this._telefonos;
    }

    set telefonos(value) {
        this._telefonos = value;
    }

    get usuario() {
        return this._usuario;
    }

    set usuario(value) {
        this._usuario = value;
    }

    get idioma() {
        return this._idioma;
    }

    set idioma(value) {
        this._idioma = value;
    }

    get isEmpresa(): boolean {
        return this._isEmpresa;
    }

    set isEmpresa(value: boolean) {
        this._isEmpresa = value;
    }

    get nombreCompleto() {
        return this._nombreCompleto;
    }

    set nombreCompleto(value) {
        this._nombreCompleto = value;
    }
}

TelefonoPersona.PERSONA_MAP.type = PropertyType.Uri(Persona);
DireccionPersona.PERSONA_MAP.type = PropertyType.Uri(Persona);
